/**
 * Client-side view of the `Region` listing field — the 26 Swiss cantons that
 * suppliers pick as their Einsatzgebiet / Lieferregion.
 *
 * The data comes from the same JSON the server reads
 * (src/containers/LandingPages/data/cantons.json), so there is one canton table
 * for the whole codebase. Only these few pure helpers exist twice, because the
 * client bundle (ESM) and server/api-util/regions.js (plain CommonJS, run by
 * node without transpilation) cannot share a module. A parity test pins both
 * sides to the same output — the same arrangement the search-param helper
 * already uses.
 */
import cantonsAsset from '../containers/LandingPages/data/cantons.json';

export const REGION_FIELD_KEY = 'Region';

const SEARCH_MODES = ['has_all', 'has_any'];

/** All 26 cantons: `{ option, label }`. */
export const CANTONS = cantonsAsset.cantons.map(({ option, label }) => ({ option, label }));

/** Which cantons border which, keyed by option. */
export const NEIGHBOURS = cantonsAsset.cantons.reduce(
  (acc, c) => ({ ...acc, [c.option]: c.neighbours }),
  {}
);

/**
 * URL slug for a canton, e.g. 'St-Gallen' -> 'st-gallen'.
 *
 * A plain lowercase of the option key. The keys are already ASCII and
 * hyphenated in Console, so there is no transliteration map to fall out of date.
 *
 * @param {string} option canton option key
 * @returns {string} slug
 */
export const regionSlug = option => String(option || '').toLowerCase();

/**
 * Canonical path of a canton landing page. The trailing slash matches the
 * canonical URL the page declares and the /kategorie/ convention.
 *
 * @param {string} option canton option key
 * @returns {string} path
 */
export const regionPath = option => `/region/${regionSlug(option)}/`;

/**
 * The region-filtered search path for a canton.
 *
 * Must stay byte-identical to `regionSearchPath` in server/api-util/regions.js
 * and to what `listingFieldSearchParam` produces, so a visitor arriving from a
 * canton page sees the filter state the site's own Region filter would set.
 *
 * @param {string} option canton option key
 * @param {string} [searchMode] 'has_all' or 'has_any'; anything else falls back
 *   to 'has_all'
 * @returns {string} path
 */
export const regionSearchPath = (option, searchMode) => {
  const mode = SEARCH_MODES.includes(searchMode) ? searchMode : 'has_all';
  return `/s?pub_${REGION_FIELD_KEY}=${mode}:${encodeURIComponent(option)}`;
};

/**
 * The region filter as a search string, e.g. '?pub_Region=has_all:Zuerich'.
 *
 * Same value regionSearchPath embeds, without the /s prefix - NamedLink builds
 * the pathname from the route table and wants only the query.
 *
 * @param {string} option canton option key
 * @param {string} [searchMode]
 * @returns {string} search string including the leading '?'
 */
export const regionSearchString = (option, searchMode) =>
  regionSearchPath(option, searchMode).replace(/^[^?]*/, '');

/**
 * Look a canton up by its option key.
 *
 * @param {string} option canton option key
 * @returns {Object|null} canton record, or null when unknown
 */
export const cantonByOption = option => CANTONS.find(c => c.option === option) || null;

/**
 * Look a canton up by its URL slug.
 *
 * @param {string} slug canton slug
 * @returns {Object|null} canton record, or null when unknown
 */
export const cantonBySlug = slug => CANTONS.find(c => regionSlug(c.option) === slug) || null;

/**
 * Neighbouring cantons as records rather than bare keys.
 *
 * @param {string} option canton option key
 * @param {number} [limit] max number returned
 * @returns {Array<Object>} canton records; empty when the key is unknown
 */
export const neighboursOf = (option, limit) => {
  const keys = NEIGHBOURS[option] || [];
  const records = keys.map(cantonByOption).filter(Boolean);
  return typeof limit === 'number' ? records.slice(0, limit) : records;
};

/**
 * The `Region` field's configured search mode, e.g. 'has_all'.
 *
 * Every writer of the region param has to read this rather than pick a mode: the sidebar
 * filter, the /region/ landing pages and the server's regionSearchPath all build the value
 * from the field config, and two writers disagreeing would make the same selection produce
 * two different URLs.
 *
 * @param {Object} config marketplace configuration
 * @returns {string} 'has_all' or 'has_any'; 'has_all' when the field is unknown
 */
export const regionSearchMode = config => {
  const field = (config?.listing?.listingFields || []).find(f => f.key === REGION_FIELD_KEY);
  // filterConfig.searchMode, not field.searchMode: validFilterConfig nests it, so the
  // top-level read this used was always undefined and this helper always returned its own
  // fallback - never the configured mode it exists to report.
  return SEARCH_MODES.includes(field?.filterConfig?.searchMode)
    ? field.filterConfig.searchMode
    : 'has_all';
};

/**
 * The `pub_Region` query param value for a set of cantons, e.g. 'has_all:Zuerich,Bern'.
 *
 * NOTE on what more than one canton means: that is decided by the field's search mode, not
 * here. Under 'has_all' — the mode this marketplace currently has configured — a two-canton
 * value asks for suppliers covering BOTH, not either. Switching the `Region` field to
 * 'has_any' in Console changes the header control and the sidebar filter together, which is
 * why neither of them picks a mode of its own.
 *
 * @param {Array<string>|string|null} options canton option keys, or null/[] to clear
 * @param {string} [searchMode] from regionSearchMode
 * @returns {string|null} null clears the param - see stringify in util/urlHelpers
 */
export const regionParamValue = (options, searchMode) => {
  const list = (Array.isArray(options) ? options : [options]).filter(Boolean);
  if (list.length === 0) {
    return null;
  }
  const mode = SEARCH_MODES.includes(searchMode) ? searchMode : 'has_all';
  return `${mode}:${list.join(',')}`;
};

/**
 * Read the cantons back out of a `pub_Region` value.
 *
 * Tolerates either search mode and a bare list with no mode prefix, since a hand-edited URL or
 * an older link can carry one. Unknown keys are dropped rather than passed through, so a stale
 * canton in a bookmark cannot put the control into a state the list cannot show.
 *
 * @param {string|null|undefined} value e.g. 'has_all:Zuerich,Bern'
 * @returns {Array<string>} canton option keys, empty when absent or all unknown
 */
export const parseRegionParam = value => {
  if (!value || typeof value !== 'string') {
    return [];
  }
  const afterMode = value.includes(':') ? value.slice(value.indexOf(':') + 1) : value;
  return afterMode.split(',').filter(option => !!cantonByOption(option));
};
